import { computed, ref } from 'vue';

import { Maybe, TimeSlotInterval } from '../graphql/schema';

import { TimeOption } from '../models/entities/timeslot.model';
import { DateWorkingMode } from '../models/entities/calendar.model';

import RentalService from '../services/rental.service';
import DateService from '../services/date.service';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useRangeDatePicker = (props, { emit }) => {
  // ----------------------------------------
  // variables
  const exclusivePicker = true;

  const showPickUpPicker = ref(false);

  const showReturnPicker = ref(false);

  const startMode = {
    enabled: exclusivePicker,
    workingMode: DateWorkingMode.START,
  };

  const endMode = {
    enabled: exclusivePicker,
    workingMode: DateWorkingMode.END,
  };

  // ----------------------------------------
  // computed
  const endCalendar = computed(() => {
    // This method should return the dealerCalendar formated for returnDatePicker;
    const endCalendar = { ...props.dealerCalendar };

    // Filtering code to have only days after today (it is useful to remove previous weekend days)
    endCalendar.availableDates = endCalendar.availableDates?.filter(
      (date: string) => props.selectedRangeDate.start <= new Date(date)
    );

    // If we don't have any unavailable future day, return with every future day available
    if (!props.firstUnavailableDate) return endCalendar;

    // If we have at least one future unavailable day,
    // filter to remove all available days after the first unavailable day
    endCalendar.availableDates = endCalendar.availableDates?.filter(
      (date: string) => new Date(date) < new Date(props.firstUnavailableDate)
    );

    return endCalendar;
  });

  const getSelectedRangeEndTime = computed((): Maybe<Date> => {
    return props.selectedRangeTime.end;
  });

  const getSelectedRangeStartTime = computed((): Maybe<Date> => {
    return props.selectedRangeTime.start;
  });

  const getTimeSlots = computed((): TimeSlotInterval => {
    const shouldApplySameDate =
      props.selectedRangeDate.start?.getTime() ===
      props.selectedRangeDate.end?.getTime();

    return {
      start: RentalService.setAvailableTime(
        props.rentalSlotTimes?.start,
        props.selectedRangeTime,
        DateWorkingMode.START,
        props.rentalTimeConfig,
        shouldApplySameDate
      ),
      end: RentalService.setAvailableTime(
        props.rentalSlotTimes?.end,
        props.selectedRangeTime,
        DateWorkingMode.END,
        props.rentalTimeConfig,
        shouldApplySameDate
      ),
    };
  });

  // ----------------------------------------
  // methods
  const changeDate = (date: Date, datePickerOrder?: DateWorkingMode) => {
    if (!datePickerOrder) {
      emit('changeDate', date);
      return;
    }

    let shouldApplySameDate = false;

    if (
      DateService.hasInvalidDatesIntoRange(
        date,
        props.activeDealerCalendar,
        props.selectedRangeDate,
        datePickerOrder
      )
    ) {
      if (datePickerOrder === DateWorkingMode.START) shouldApplySameDate = true;
      else return;
    }

    validateDates(date, datePickerOrder, shouldApplySameDate);

    if (
      props.selectedRangeDate.start.getTime() ===
      props.selectedRangeDate.end.getTime()
    ) {
      shouldApplySameDate = true;
    }

    if (shouldApplySameDate) recreateAvailableTimes();
    emit('getRentalPrice');
  };

  const changeTime = (
    time: TimeOption,
    datePickerOrder: DateWorkingMode
  ): void => {
    emit('changeSelectedRangeTime', {
      ...props.selectedRangeTime,
      [datePickerOrder]: time,
    });

    emit('getRentalPrice');
  };

  const controlReturnPicker = (value: boolean): void => {
    showPickUpPicker.value = value;
    showReturnPicker.value = false;
  };

  const controlPickUpPicker = (value: boolean): void => {
    showReturnPicker.value = value;
    showPickUpPicker.value = false;
  };

  const maskCalendarSection = (): void => {
    emit('maskCalendar');
  };

  const onPickerToggle = (
    value: boolean,
    workingMode: DateWorkingMode
  ): void => {
    if (workingMode === DateWorkingMode.END) {
      controlPickUpPicker(value);
    } else {
      controlReturnPicker(value);
    }
  };

  const recreateAvailableTimes = (): void => {
    if (
      getTimeSlots.value &&
      getTimeSlots.value.start.length &&
      getTimeSlots.value.end.length
    ) {
      const startIndex = getTimeSlots.value.start.findIndex(
        (time: TimeOption) =>
          time.value === props.selectedRangeTime?.start?.value
      );
      const endIndex = getTimeSlots.value.end.findIndex(
        (time: TimeOption) => time.value === props.selectedRangeTime?.end?.value
      );
      if (endIndex <= startIndex) {
        emit('changeSelectedRangeTime', {
          ...props.selectedRangeTime,
          start: getTimeSlots.value.start[0],
          end: getTimeSlots.value.end[getTimeSlots.value.end.length - 1],
        });
      }
    }
  };

  const unmaskCalendarSection = (): void => {
    emit('unmaskCalendar');
  };

  const validateDates = (
    date: Date | string,
    datePickerOrder: DateWorkingMode,
    shouldApplySameDate: boolean
  ): void => {
    // Important to do a deep copy and reset hours because we are basically comparing timestamps
    const dayForComparison = DateService.resetHours(date);
    const startDate = DateService.resetHours(
      props.selectedRangeDate.start || ''
    );
    const endDate = DateService.resetHours(props.selectedRangeDate.end || '');

    const startCondition =
      datePickerOrder === DateWorkingMode.START || startDate > dayForComparison;

    const endCondition =
      datePickerOrder === DateWorkingMode.END ||
      endDate < dayForComparison ||
      shouldApplySameDate;

    emit('changeSelectedRangeDate', {
      start: startCondition ? date : props.selectedRangeDate.start,
      end: endCondition ? date : props.selectedRangeDate.end,
    });
  };

  const changeActiveTextCarouselSlide = (
    index: number,
    mode: DateWorkingMode
  ): void => {
    emit('changeActiveTextCarouselSlideRange', {
      ...props.activeTextCarouselSlideRange,
      [mode]: index,
    });
  };

  return {
    changeActiveTextCarouselSlide,
    changeDate,
    changeTime,
    controlReturnPicker,
    controlPickUpPicker,
    endCalendar,
    endMode,
    getSelectedRangeEndTime,
    getSelectedRangeStartTime,
    getTimeSlots,
    maskCalendarSection,
    onPickerToggle,
    showPickUpPicker,
    showReturnPicker,
    startMode,
    unmaskCalendarSection,
    validateDates,
  };
};

export default useRangeDatePicker;
